import { fetchCustomerSupportResponse } from "@/services/schaden-ki/fetch-customer-support-response";
import { ChatStore } from "@/stores/ChatStore";
import SettingsStore from "@/stores/SettingsStore";
import type { ResponseInputAudioTranscriptionCompleted } from "@/types";
import { featureFlags } from "@/lib/featureFlags";

const BLOCKBRAIN_TRIGGER_REGEX = /\bblock\s*brain\b/i;
// User explicitly leaves the Blockbrain context and wants to return to SchadenKI / claim handling
const BLOCKBRAIN_EXIT_REGEX = /\b(zur(ü|ue)ck zur schadenmeldung|schadenmeldung starten|stop blockbrain|exit blockbrain|blockbrain beenden|raus aus blockbrain)\b/i;

export default function onReceivedInputAudioTranscriptionCompleted(message: ResponseInputAudioTranscriptionCompleted) {
  console.log("Transcript:", message);

  // The transcript is already a plain string, no need to parse it
  let processedTranscript = message.transcript;

  // this is a workaround for the fact that gpt-4o-transcribe does not add a period at the end of the transcript
  if (processedTranscript?.[processedTranscript.length - 1]?.match(/[a-zA-Z0-9]/)) {
    processedTranscript += ".";
  }

  ChatStore.getState().updateLastMessageTranscript(processedTranscript, "user");

  const useSchadenKI = SettingsStore.getState().useSchadenKI;
  const blockbrainEnabled = featureFlags.blockbrain;
  const text = processedTranscript || "";
  const hasTrigger = blockbrainEnabled && BLOCKBRAIN_TRIGGER_REGEX.test(text);
  const wantsExit = blockbrainEnabled && BLOCKBRAIN_EXIT_REGEX.test(text);
  let inBlockbrain = ChatStore.getState().inBlockbrainConversation;

  if (wantsExit && inBlockbrain) {
    console.log("[Blockbrain] Exit phrase detected - returning to SchadenKI");
    ChatStore.getState().setInBlockbrainConversation(false);
    inBlockbrain = false;
  } else if (hasTrigger && !inBlockbrain) {
    console.log("[Blockbrain] Trigger detected - entering sticky Blockbrain mode");
    ChatStore.getState().setInBlockbrainConversation(true);
    inBlockbrain = true;
  }

  if (blockbrainEnabled && (hasTrigger || inBlockbrain)) {
    // Hand the user's already-transcribed turn to the Realtime API so the LLM
    // can call the ask_blockbrain tool, regardless of SchadenKI being active.
    console.log("[Blockbrain] Routing transcript to Realtime API", { hasTrigger, sticky: inBlockbrain });
    const trigger = ChatStore.getState().triggerRealtimeResponse;
    if (trigger) {
      ChatStore.getState().setAvatarStatus("Frage Blockbrain");
      ChatStore.getState().setIsWaitingForBlockbrain(true);
      // Safety net: clear the spinner after 180 s. With streaming + the
      // conciseness hint Blockbrain typically answers within ~10-30 s, but we
      // keep generous head-room for deep follow-up questions.
      window.setTimeout(() => {
        if (ChatStore.getState().isWaitingForBlockbrain) {
          console.warn("[Blockbrain] Timeout (180s) - clearing waiting state");
          ChatStore.getState().setIsWaitingForBlockbrain(false);
        }
      }, 180_000);
      trigger();
      return;
    }
    console.warn("[Blockbrain] triggerRealtimeResponse is not wired in ChatStore yet");
  }

  if (useSchadenKI) {
    ChatStore.getState().setAvatarStatus("Denken");
    fetchCustomerSupportResponse(processedTranscript);
  }
}
